/**
 * Maps classic predefinedGameComponents settings → PlayEngine-style FieldRows.
 * Same gameConfig paths / values as Version Settings; different UI chrome only.
 */

import React from "react";
import _ from "lodash";
import { MenuItem, Select } from "@material-ui/core";
import {
  FieldRow,
  NumberConfigField,
  ColorConfigField,
  StringConfigField,
  BooleanConfigField,
  GridAreaConfigField,
} from "./sceneEditorInspector";
import VersionComponentSettingsEditors from "../versionComponentSettingsEditors";
import { pushPreviewPatch, buildComponentPatch } from "../../modules/previewPatch";
import { rewriteComponentPathForLocalizationOverride } from "../../modules/localizationOverrides";
import { sceneEditorWriteNumberPath } from "../../modules/sceneEditorGestureWrite";

const CARD_BG = "#474747";

/** Settings rendered by scene proto FieldRows (not classic VersionComponentSettingsEditors). */
function isProtoHandledSetting(setting) {
  const t = _.get(setting, "editorType");
  if (t === "switchGeneric" || t === "switchGenericFullPath") return true;
  if (t === "color" || t === "selectorColor") return true;
  if (t === "editorText" || t === "caption" || t === "editorTextArea" || t === "editorGridArea") return true;
  if (t === "number") return true;
  if (t === "sliderGeneric") return true;
  if (
    (t === "selectorString" || t === "selectorLevelType" || t === "selectorRibbonType") &&
    Array.isArray(setting.items) &&
    setting.items.length > 0
  ) {
    return true;
  }
  return false;
}

/**
 * Group settings so classic/media runs share one VersionComponentSettingsEditors call
 * (legacy TreeView style), while proto fields stay per-row.
 */
function buildSettingSegments(settingsList) {
  const segments = [];
  let classicRun = null;
  settingsList.forEach((setting, index) => {
    if (isProtoHandledSetting(setting)) {
      if (classicRun) {
        segments.push(classicRun);
        classicRun = null;
      }
      segments.push({ kind: "proto", setting, index });
      return;
    }
    if (!classicRun) {
      classicRun = { kind: "classic", settings: [], startIndex: index };
    }
    classicRun.settings.push(setting);
  });
  if (classicRun) segments.push(classicRun);
  return segments;
}

function renderClassicSettingsBatch({
  component,
  settings: classicSettings,
  batchKey,
  props,
  classes,
  setValue,
  realSelectedVersion,
}) {
  if (!classicSettings.length) return null;
  const readOnly = props.userPermissions?.update === false;
  return (
    <div key={batchKey} className="scene-editor-classic-fallback scene-editor-classic-batch">
      <VersionComponentSettingsEditors
        component={{ ...component, settings: classicSettings }}
        props={props}
        classes={classes}
        setValue={setValue}
        realSelectedVersion={realSelectedVersion}
        // The FieldRow wrapper below already renders the tooltip in the inspector's
        // own style — without this the classic editor drew a second, off-theme one.
        hideInlineTooltips
        wrapSetting={(editor, setting) => (
          <FieldRow
            key={`${component.id}.${setting.path}.${setting.editorType}`}
            label={displaySettingCaption(setting, component)}
            tooltip={setting.tooltip}
            readOnly={readOnly}
          >
            <div className="scene-editor-classic-control">{editor}</div>
          </FieldRow>
        )}
      />
    </div>
  );
}

function renderSettingSegments({
  component,
  settingsList,
  props,
  classes,
  setValue,
  realSelectedVersion,
  getSettingPath,
  keyPrefix = "",
}) {
  // Localization must stay per-field — classic editors write the wrong paths.
  if (typeof getSettingPath === "function") {
    return settingsList.map((setting) =>
      renderSceneEditorSettingField({
        component,
        setting,
        props,
        classes,
        setValue,
        realSelectedVersion,
        getSettingPath,
      })
    );
  }

  return buildSettingSegments(settingsList).map((segment) => {
    if (segment.kind === "proto") {
      return renderSceneEditorSettingField({
        component,
        setting: segment.setting,
        props,
        classes,
        setValue,
        realSelectedVersion,
        getSettingPath,
      });
    }
    return renderClassicSettingsBatch({
      component,
      settings: segment.settings,
      batchKey: `${keyPrefix}classic-${component.id}-${segment.startIndex}-${segment.settings[0].path}`,
      props,
      classes,
      setValue,
      realSelectedVersion,
    });
  });
}

function guardUpdate(props, fn) {
  if (props.userPermissions?.update === false) {
    props.createSnackbar?.(
      "You do not have permission to update, contact your administrator.",
      "warning",
      false
    );
    return;
  }
  fn();
}

function resolveValuePath(component, setting, getSettingPath) {
  if (setting.gameConfigPath) {
    return setting.gameConfigPath;
  }
  if (typeof getSettingPath === "function") {
    return getSettingPath(component, setting);
  }
  if (setting.editorSubType && String(setting.editorSubType).includes("FullPath")) {
    return `gameConfig.components.${setting.path}`;
  }
  if (setting.editorType === "switchGenericFullPath" || setting.editorType === "selectorImageFullPath") {
    return `gameConfig.components.${setting.path}`;
  }
  return `gameConfig.components.${component.id}.${setting.path}`;
}

function readValue(props, component, setting, fallback, getSettingPath) {
  const path = resolveValuePath(component, setting, getSettingPath).replace(
    /^gameConfig\./,
    "selectedVersion.gameConfig."
  );
  return _.get(props, path, fallback);
}

function toDisplayHex(value) {
  if (value == null || value === "") return "#000000";
  if (typeof value === "string") {
    if (value.startsWith("#")) return value;
    if (value.startsWith("0x") || value.startsWith("0X")) {
      return `#${value.slice(2).padStart(6, "0")}`;
    }
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return "#000000";
  return `#${(n & 0xffffff).toString(16).padStart(6, "0")}`;
}

function fromDisplayHex(hex, preferNumber = true) {
  const cleaned = String(hex || "#000000");
  if (!preferNumber) return cleaned;
  return parseInt(cleaned.replace("#", "0x"), 16);
}

function orientationTag(isPortrait) {
  return (
    <span
      style={{
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        color: "#8a8d93",
        display: "block",
        width: "100%",
      }}
    >
      {isPortrait ? "Portrait" : "Landscape"}
    </span>
  );
}

function ProtoSelect({ value, disabled, items, onChange }) {
  return (
    <Select
      fullWidth
      size="small"
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.stopPropagation()}
      MenuProps={{ PaperProps: { style: { backgroundColor: CARD_BG, maxHeight: 320 } } }}
      style={{ color: "#fff", fontSize: 12 }}
    >
      {(items || []).map((item) => {
        const v = typeof item === "object" ? item.value ?? item : item;
        const label = typeof item === "object" ? item.label ?? item.value ?? item : item;
        return (
          <MenuItem key={String(v)} value={v} style={{ color: "#eee", fontSize: 12 }}>
            {label}
          </MenuItem>
        );
      })}
    </Select>
  );
}

function isCaptionVisibleForOrientation(caption, isPortrait) {
  const label = String(caption || "");
  const hasPortrait = label.includes("Portrait");
  const hasLandscape = label.includes("Landscape");
  if (!hasPortrait && !hasLandscape) return true;
  if (isPortrait && hasPortrait) return true;
  if (!isPortrait && hasLandscape) return true;
  return false;
}

function clamp(n, min, max) {
  let v = n;
  if (Number.isFinite(min)) v = Math.max(min, v);
  if (Number.isFinite(max)) v = Math.min(max, v);
  return v;
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Drop redundant group prefix: "Debug Point 2 Rotation" → "Rotation". */
function displaySettingCaption(setting, component) {
  const caption = setting.caption || setting.path || "";
  const groupName = String(
    ((component.isGroup || component.isSubGroup) && component.caption) || setting.group || ""
  ).trim();
  if (!groupName) return caption;
  const stripped = caption
    .replace(new RegExp(`^${escapeRegExp(groupName)}(?:\\s+|\\s*[-–:]\\s*)`, "i"), "")
    .trim();
  return stripped || caption;
}

/**
 * Render one setting with PlayEngine field chrome.
 * Unsupported/complex types fall back to classic editors (same data path).
 */
export function renderSceneEditorSettingField({
  component,
  setting,
  props,
  classes,
  setValue,
  realSelectedVersion,
  getSettingPath,
}) {
  if (setting.visibleWhen) {
    const visSettingPath = getSettingPath
      ? getSettingPath(component, { ...setting, path: setting.visibleWhen.path })
      : `gameConfig.components.${component.id}.${setting.visibleWhen.path}`;
    let visibleValue = _.get(
      props,
      visSettingPath.replace(/^gameConfig\./, "selectedVersion.gameConfig.")
    );
    if (visibleValue == null || visibleValue === "") {
      visibleValue = setting.visibleWhen.default;
    }
    if (String(visibleValue) !== String(setting.visibleWhen.equals)) {
      return null;
    }
  }

  const readOnly = props.userPermissions?.update === false;
  const rawCaption = setting.caption || setting.path;
  const label = displaySettingCaption(setting, component);
  const key = `${component.id}.${setting.path}.${setting.editorType}`;
  const isPortrait = props.newSetPortrait !== false;
  if (!isCaptionVisibleForOrientation(rawCaption, isPortrait)) {
    return null;
  }
  const basePath = resolveValuePath(component, setting, getSettingPath);

  const write = (path, value, options) => {
    guardUpdate(props, () => {
      if (options && (options.gesture || options.gestureEnd)) {
        sceneEditorWriteNumberPath({
          path,
          value,
          options,
          setValue,
          selectedVersion: realSelectedVersion,
          selectedBase: props.selectedBase,
          selectedLanguage:
            props.selectedLanguage ||
            _.get(realSelectedVersion, "gameConfig.options.language", "en"),
          isLocalization: typeof getSettingPath === "function",
          rewritePayload: rewriteComponentPathForLocalizationOverride,
        });
        return;
      }
      setValue({ path, value });
    });
  };

  const writeBase = (value, options) => {
    write(basePath, value, options);
    if (setting.gameConfigPath === "gameConfig.options.isSoundEnabled") {
      try {
        document.getElementById("gamePreviewIframe").focus();
      } catch (err) {
        console.error(err);
      }
    }
  };
  const current = (fallback) => readValue(props, component, setting, fallback, getSettingPath);

  // ── boolean ──────────────────────────────────────────────────────────────
  if (setting.editorType === "switchGeneric" || setting.editorType === "switchGenericFullPath") {
    const value = current(false);
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <BooleanConfigField value={value} disabled={readOnly} onChange={writeBase} />
      </FieldRow>
    );
  }

  // ── color (stored as 0x number or #hex string) ────────────────────────────
  if (setting.editorType === "color" || setting.editorType === "selectorColor") {
    const raw = current(setting.editorType === "selectorColor" ? "#ffffff" : 0xffffff);
    const storeAsNumber = typeof raw === "number" || setting.editorType === "color";
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <ColorConfigField
          value={toDisplayHex(raw)}
          disabled={readOnly}
          onCommit={(hex) => writeBase(fromDisplayHex(hex, storeAsNumber))}
        />
      </FieldRow>
    );
  }

  // ── grid / drop strings: preview row + arrange/separator/Save dialog ──────
  if (setting.editorType === "editorGridArea") {
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <GridAreaConfigField
          value={current("")}
          disabled={readOnly}
          label={rawCaption}
          // Same key the classic panel uses, so the chosen separator carries over.
          storageKey={`${_.get(props, "selectedBase._id", "")}.${_.get(
            props,
            "selectedVersion._id",
            ""
          )}.${component.id}.${setting.path}`}
          onCommit={writeBase}
          onPreview={(value) => {
            const patch = buildComponentPatch(props.selectedVersion, basePath, value);
            if (patch) pushPreviewPatch(patch);
          }}
        />
      </FieldRow>
    );
  }

  // ── text (Enter inserts a line break; grows with content) ────────────────
  if (
    setting.editorType === "editorText" ||
    setting.editorType === "caption" ||
    setting.editorType === "editorTextArea"
  ) {
    const value = current("");
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <StringConfigField value={value ?? ""} disabled={readOnly} onChange={writeBase} />
      </FieldRow>
    );
  }

  if (setting.editorType === "number") {
    const value = current(0);
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <NumberConfigField
          value={value}
          disabled={readOnly}
          step={setting.step}
          min={setting.min}
          max={setting.max}
          onChange={(num, opts) => writeBase(clamp(num, setting.min, setting.max), opts)}
        />
      </FieldRow>
    );
  }

  // ── dropdowns (only when items are declared on the setting) ──────────────
  if (
    (
      setting.editorType === "selectorString" ||
      setting.editorType === "selectorLevelType" ||
      setting.editorType === "selectorRibbonType"
    ) &&
    Array.isArray(setting.items) &&
    setting.items.length > 0
  ) {
    const value = current(setting.items?.[0] ?? "");
    const items = setting.items || [];
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <ProtoSelect
          value={value}
          disabled={readOnly}
          items={items}
          onChange={writeBase}
        />
      </FieldRow>
    );
  }

  // ── sliderGeneric → PlayEngine number / vec2 / responsive fields ─────────
  if (setting.editorType === "sliderGeneric") {
    const value = current(0);
    const sub = setting.editorSubType || "";
    const min = setting.min;
    const max = setting.max;

    const numField = (num, onChange, axisLabel) => (
      <NumberConfigField
        value={num}
        disabled={readOnly}
        label={axisLabel}
        step={setting.step}
        min={min}
        max={max}
        onChange={(n, opts) => onChange(clamp(n, min, max), opts)}
      />
    );

    // plain number
    if (!sub || sub === "singleSliderFullPath") {
      return (
        <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
          {numField(typeof value === "number" ? value : Number(value) || 0, writeBase)}
        </FieldRow>
      );
    }

    // responsive scalar { portrait, landscape }
    if (sub === "WHR_2" || sub === "WHR_2_FullPath") {
      const portrait = _.get(value, "portrait", typeof value === "number" ? value : 0);
      const landscape = _.get(value, "landscape", typeof value === "number" ? value : 0);
      const current = isPortrait ? portrait : landscape;
      return (
        <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
          {orientationTag(isPortrait)}
          {numField(current, (n, opts) => {
            write(`${basePath}.${isPortrait ? "portrait" : "landscape"}`, n, opts);
          })}
        </FieldRow>
      );
    }

    // vec2 { x, y }
    if (sub === "XY_2" || sub === "XY_2_FullPath") {
      const x = _.get(value, "x", 0);
      const y = _.get(value, "y", 0);
      return (
        <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {numField(x, (n, opts) => write(`${basePath}.x`, n, opts), "x")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {numField(y, (n, opts) => write(`${basePath}.y`, n, opts), "y")}
            </div>
          </div>
        </FieldRow>
      );
    }

    // responsive vec2
    if (sub === "XY_4" || sub === "XY_4_FullPath") {
      const orient = isPortrait ? "portrait" : "landscape";
      const x = _.get(value, `${orient}.x`, 0);
      const y = _.get(value, `${orient}.y`, 0);
      if (setting.y_axis) {
        return (
          <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
            {orientationTag(isPortrait)}
            {numField(y, (n, opts) => write(`${basePath}.${orient}.y`, n, opts), "y")}
          </FieldRow>
        );
      }
      return (
        <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
          {orientationTag(isPortrait)}
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {numField(x, (n, opts) => write(`${basePath}.${orient}.x`, n, opts), "x")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {numField(y, (n, opts) => write(`${basePath}.${orient}.y`, n, opts), "y")}
            </div>
          </div>
        </FieldRow>
      );
    }

    // responsive size { width, height }
    if (sub === "WHR_4" || sub === "WHR_4_FullPath") {
      const orient = isPortrait ? "portrait" : "landscape";
      const width = _.get(value, `${orient}.width`, 0);
      const height = _.get(value, `${orient}.height`, 0);
      if (setting.y_axis) {
        return (
          <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
            {orientationTag(isPortrait)}
            {numField(height, (n, opts) => write(`${basePath}.${orient}.height`, n, opts), "h")}
          </FieldRow>
        );
      }
      return (
        <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
          {orientationTag(isPortrait)}
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {numField(width, (n, opts) => write(`${basePath}.${orient}.width`, n, opts), "w")}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {numField(height, (n, opts) => write(`${basePath}.${orient}.height`, n, opts), "h")}
            </div>
          </div>
        </FieldRow>
      );
    }
  }

  // ── fallback: classic editor(s) for media / specialty types ──────────────
  // Localization uses proto chrome only — classic version editors write the wrong paths.
  if (typeof getSettingPath === "function") {
    const value = current("");
    return (
      <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
        <StringConfigField value={value ?? ""} disabled={readOnly} onChange={writeBase} />
      </FieldRow>
    );
  }

  return (
    <FieldRow key={key} label={label} tooltip={setting.tooltip} readOnly={readOnly}>
      <div className="scene-editor-classic-fallback">
        <VersionComponentSettingsEditors
          component={{ ...component, settings: [setting] }}
          props={props}
          classes={classes}
          setValue={setValue}
          realSelectedVersion={realSelectedVersion}
        />
      </div>
    </FieldRow>
  );
}

/**
 * Render all settings for a versionSettings component with proto chrome.
 */
export default function SceneEditorSettingsList({
  component,
  props,
  classes,
  setValue,
  realSelectedVersion,
  getSettingPath,
}) {
  const settings = _.get(component, "settings") || [];
  if (!component || settings.length === 0) return null;
  return (
    <>
      {renderSettingSegments({
        component,
        settingsList: settings,
        props,
        classes,
        setValue,
        realSelectedVersion,
        getSettingPath,
      })}
    </>
  );
}
